import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  SparklesIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  PlusIcon,
  TrashIcon,
  CalendarDaysIcon,
  ClipboardDocumentListIcon,
  DocumentPlusIcon,
  PencilSquareIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_PREFIX = 'customerAssistantChats_v1';

/** Starter prompts aligned with customerAssistantService tools (list/search claims, appointments, reference_data, writes).
 *  displayText is what the user sees in the chat bubble and chat title.
 *  prompt is the detailed tool-directing text sent to the backend.
 *  suggestedOn is a list of path prefixes where this starter should be visually highlighted. */
const QUICK_START_ACTIONS = [
  {
    id: 'schedule',
    label: 'Schedule an appointment',
    shortLabel: 'Schedule',
    displayText: 'Schedule a new appointment',
    Icon: CalendarDaysIcon,
    suggestedOn: ['/appointments/new'],
    prompt:
      'I want to schedule a new interpreter appointment. Please walk me through it step by step right here in chat — ask me one question at a time (claimant, claim if any, date, time, duration, remote or on-site address, service type, language, title, special requirements), then show me a summary and book it after I confirm with create_appointment. After it is booked, show me the appointment details with the job number.'
  },
  {
    id: 'status',
    label: 'Check on appointment status',
    shortLabel: 'Status',
    displayText: 'Check my appointment status',
    Icon: ClipboardDocumentListIcon,
    suggestedOn: ['/appointments', '/dashboard'],
    prompt:
      'Please show my upcoming appointments first, then a short list of recent appointments. Use list_my_appointments with upcoming_only true for upcoming, then again with upcoming_only false for recent. Explain what each status means in plain language.'
  },
  {
    id: 'new_claim',
    label: 'Create new claim',
    shortLabel: 'New claim',
    displayText: 'Create a new claim',
    Icon: DocumentPlusIcon,
    suggestedOn: ['/claims', '/claimants'],
    prompt:
      'I want to create a new claim. Walk me step by step: if I need a new claimant first, use create_claimant and confirm names with me; then use check_claim_number before create_claim. Ask for case type and claim number clearly before creating.'
  },
  {
    id: 'edit',
    label: 'Edit an appointment',
    shortLabel: 'Edit',
    displayText: 'Edit an appointment',
    Icon: PencilSquareIcon,
    suggestedOn: [],
    prompt:
      'I need to change an existing appointment (date, time, duration, location, or other details). List my upcoming and recent appointments so I can pick one. Explain that edits are completed in the portal under Appointments — open the appointment from the list — and tell me what information I should have ready when I edit.'
  }
];

function storageKey(userKey) {
  return `${STORAGE_PREFIX}_${userKey}`;
}

function newChatId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `chat_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function deriveChatTitle(messages) {
  const firstUser = messages.find((m) => m.role === 'user');
  if (!firstUser) return 'New chat';
  const raw = firstUser.displayContent || firstUser.content;
  if (!raw) return 'New chat';
  const t = String(raw).trim();
  if (!t) return 'New chat';
  return t.length <= 44 ? t : `${t.slice(0, 44)}…`;
}

function FormattedAssistantText({ text }) {
  const blocks = useMemo(() => {
    if (!text) return [];
    return text.split(/\n\n+/).map((para, i) => {
      const lines = para.split('\n');
      const isBulletBlock = lines.every((l) => !l.trim() || l.trim().startsWith('- ') || l.trim().startsWith('• '));
      if (isBulletBlock && lines.some((l) => l.trim())) {
        return (
          <ul key={i} className="list-disc pl-4 space-y-1 my-2 text-[13px] leading-relaxed">
            {lines
              .filter((l) => l.trim())
              .map((line, j) => (
                <li key={j} className="text-gray-800">
                  <InlineBold text={line.replace(/^\s*[-•]\s+/, '')} />
                </li>
              ))}
          </ul>
        );
      }
      return (
        <p key={i} className="text-[13px] leading-relaxed text-gray-800 my-1.5 first:mt-0 last:mb-0">
          <InlineBold text={para} />
        </p>
      );
    });
  }, [text]);

  return <div className="break-words">{blocks}</div>;
}

function InlineBold({ text }) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    if (m) {
      return (
        <strong key={idx} className="font-semibold text-gray-900">
          {m[1]}
        </strong>
      );
    }
    return <span key={idx}>{part}</span>;
  });
}

/**
 * Pull out fenced ```options blocks containing a JSON array of { label, send }.
 * Returns the visible text with those blocks stripped, plus an array of option groups.
 */
function parseOptionsBlocks(text) {
  if (!text) return { stripped: text || '', blocks: [] };
  const blocks = [];
  const re = /```options\s*\n?([\s\S]*?)```/gi;
  const stripped = String(text).replace(re, (_match, body) => {
    try {
      const parsed = JSON.parse(String(body).trim());
      if (Array.isArray(parsed)) {
        const options = parsed
          .map((o) => {
            if (!o || typeof o !== 'object') return null;
            const label = typeof o.label === 'string' ? o.label.trim() : '';
            const send = typeof o.send === 'string' ? o.send.trim() : label;
            if (!label || !send) return null;
            return { label, send };
          })
          .filter(Boolean);
        if (options.length > 0) blocks.push({ options });
      }
    } catch (_) {
      /* malformed JSON — drop the block silently */
    }
    return '';
  });
  return { stripped: stripped.replace(/\n{3,}/g, '\n\n').trim(), blocks };
}

function AssistantOptionButtons({ blocks, disabled, onPick }) {
  if (!blocks || blocks.length === 0) return null;
  return (
    <div className="mt-2 space-y-2">
      {blocks.map((block, bi) => (
        <div key={bi} className="flex flex-col gap-1.5">
          {block.options.map((opt, oi) => (
            <button
              key={`${bi}-${oi}`}
              type="button"
              disabled={disabled}
              onClick={() => onPick(opt)}
              className="w-full rounded-xl border border-violet-200 bg-white px-3 py-2 text-left text-[12px] font-medium text-gray-800 shadow-sm transition-colors hover:border-violet-300 hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {opt.label}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Extract simple navigation chips from an assistant reply.
 * We do not have appointment UUIDs in the reply text, so specific job numbers
 * link to the Appointments list. The goal is tap-to-navigate, not precision deep-linking.
 */
function extractActionChips(replyText) {
  if (!replyText) return [];
  const text = String(replyText);
  const chips = [];
  const seen = new Set();
  const push = (chip) => {
    if (seen.has(chip.key)) return;
    seen.add(chip.key);
    chips.push(chip);
  };

  const jobRe = /\bO-?\d{5,}\b/gi;
  let m;
  while ((m = jobRe.exec(text)) !== null) {
    const raw = m[0];
    const digits = raw.replace(/[^0-9]/g, '');
    const normalized = `O-${digits}`;
    push({
      key: `job:${normalized}`,
      label: `Find ${normalized}`,
      to: '/appointments'
    });
    if (chips.length >= 3) break;
  }

  const mentionsAppt = /\bappointment|\bjob\b|\bscheduled\b/i.test(text);
  const mentionsClaim = /\bclaim\b/i.test(text);
  const mentionsClaimant = /\bclaimant\b/i.test(text);
  const mentionsNewAppt = /\bnew appointment|\bschedule\b|\bbook\b/i.test(text);

  if (mentionsNewAppt) {
    push({ key: 'new-appt', label: 'New Appointment', to: '/appointments/new' });
  }
  if (mentionsAppt) {
    push({ key: 'all-appts', label: 'Go to Appointments', to: '/appointments' });
  }
  if (mentionsClaim) {
    push({ key: 'all-claims', label: 'Go to Claims', to: '/claims' });
  }
  if (mentionsClaimant) {
    push({ key: 'all-claimants', label: 'Go to Claimants', to: '/claimants' });
  }

  return chips.slice(0, 4);
}

function AssistantActionChips({ chips, onNavigate }) {
  if (!chips || chips.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={() => onNavigate(chip.to)}
          className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-medium text-violet-800 transition-colors hover:border-violet-300 hover:bg-violet-100"
        >
          <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden />
          <span className="truncate">{chip.label}</span>
        </button>
      ))}
    </div>
  );
}

const ENABLE_UI =
  typeof process.env.REACT_APP_ENABLE_CUSTOMER_ASSISTANT === 'undefined' ||
  process.env.REACT_APP_ENABLE_CUSTOMER_ASSISTANT === '' ||
  process.env.REACT_APP_ENABLE_CUSTOMER_ASSISTANT === '1' ||
  process.env.REACT_APP_ENABLE_CUSTOMER_ASSISTANT === 'true';

function isSupervisorRole(customer) {
  const r = customer?.role;
  return r === 'supervisor' || r === 'admin';
}

/**
 * Supervisor / admin only — claims & appointments Q&A via POST /customer/assistant/chat
 */
const CustomerAssistantChat = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, isAuthenticated, makeAuthenticatedRequest } = useAuth();
  const [open, setOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const listEndRef = useRef(null);

  const suggestedStarterId = useMemo(() => {
    const path = location?.pathname || '';
    const matches = QUICK_START_ACTIONS.filter((a) =>
      (a.suggestedOn || []).some((prefix) => path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(prefix))
    );
    if (matches.length === 0) return null;
    matches.sort((a, b) => {
      const lenA = Math.max(...(a.suggestedOn || []).map((p) => p.length));
      const lenB = Math.max(...(b.suggestedOn || []).map((p) => p.length));
      return lenB - lenA;
    });
    return matches[0].id;
  }, [location?.pathname]);

  const userKey = customer?.id || localStorage.getItem('customerId') || 'anonymous';

  const messages = useMemo(() => {
    const c = chats.find((x) => x.id === activeChatId);
    return c?.messages ?? [];
  }, [chats, activeChatId]);

  const sortedChats = useMemo(
    () => [...chats].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
    [chats]
  );

  useEffect(() => {
    if (!isAuthenticated || !isSupervisorRole(customer)) return;
    try {
      let raw = localStorage.getItem(storageKey(userKey));
      if (!raw && userKey !== 'anonymous') {
        const anon = localStorage.getItem(storageKey('anonymous'));
        if (anon) {
          raw = anon;
          localStorage.removeItem(storageKey('anonymous'));
          localStorage.setItem(storageKey(userKey), anon);
        }
      }
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed.chats) && parsed.chats.length > 0) {
          setChats(parsed.chats);
          const nextActive =
            parsed.activeChatId && parsed.chats.some((c) => c.id === parsed.activeChatId)
              ? parsed.activeChatId
              : parsed.chats[0].id;
          setActiveChatId(nextActive);
          setHydrated(true);
          return;
        }
      }
    } catch (_) {
      /* ignore */
    }
    const id = newChatId();
    const initial = [{ id, title: 'New chat', messages: [], updatedAt: Date.now() }];
    setChats(initial);
    setActiveChatId(id);
    setHydrated(true);
  }, [isAuthenticated, customer, userKey]);

  useEffect(() => {
    if (!hydrated || !isAuthenticated || !isSupervisorRole(customer)) return;
    if (chats.length === 0) return;
    try {
      localStorage.setItem(
        storageKey(userKey),
        JSON.stringify({ chats, activeChatId })
      );
    } catch (_) {
      toast.error('Could not save assistant chats (storage full?)');
    }
  }, [hydrated, chats, activeChatId, userKey, isAuthenticated, customer]);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open, activeChatId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const updateActiveChat = useCallback((updater) => {
    setChats((prev) =>
      prev.map((c) => {
        if (c.id !== activeChatId) return c;
        const next = typeof updater === 'function' ? updater(c) : updater;
        const msgs = next.messages ?? c.messages ?? [];
        return {
          ...c,
          ...next,
          messages: msgs,
          updatedAt: Date.now(),
          title: deriveChatTitle(msgs)
        };
      })
    );
  }, [activeChatId]);

  const handleNewChat = useCallback(() => {
    const id = newChatId();
    setChats((prev) => [{ id, title: 'New chat', messages: [], updatedAt: Date.now() }, ...prev]);
    setActiveChatId(id);
    setInput('');
  }, []);

  const handleDeleteChat = useCallback(
    (e, chatId) => {
      e.stopPropagation();
      if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
      setChats((prev) => {
        const next = prev.filter((c) => c.id !== chatId);
        if (next.length === 0) {
          const id = newChatId();
          setActiveChatId(id);
          return [{ id, title: 'New chat', messages: [], updatedAt: Date.now() }];
        }
        if (activeChatId === chatId) {
          setActiveChatId(next[0].id);
        }
        return next;
      });
      toast.success('Conversation deleted');
    },
    [activeChatId]
  );

  const submitUserMessage = useCallback(
    async (rawText, opts = {}) => {
      const text = String(rawText ?? '').trim();
      if (!text || loading || !activeChatId) return;

      const displayText = opts.displayText ? String(opts.displayText).trim() : '';
      const uiMsg = { role: 'user', content: text };
      if (displayText && displayText !== text) {
        uiMsg.displayContent = displayText;
      }

      const nextMessages = [...messages, uiMsg];
      updateActiveChat((c) => ({ ...c, messages: nextMessages }));
      setInput('');
      setLoading(true);

      try {
        const apiMessages = nextMessages.map(({ role, content }) => ({ role, content }));
        const body = await makeAuthenticatedRequest('/assistant/chat', {
          method: 'POST',
          body: JSON.stringify({ messages: apiMessages })
        });

        if (!body.success || body.data?.reply === undefined) {
          throw new Error(body.message || 'No reply from assistant');
        }

        const withReply = [...nextMessages, { role: 'assistant', content: body.data.reply }];
        updateActiveChat((c) => ({ ...c, messages: withReply }));
      } catch (e) {
        toast.error(e.message || 'Assistant request failed');
        const errReply = [
          ...nextMessages,
          {
            role: 'assistant',
            content: `Could not get a response: ${e.message || 'Unknown error'}. If this persists, confirm ANTHROPIC_API_KEY on the server and ENABLE_CUSTOMER_ASSISTANT is not disabled.`
          }
        ];
        updateActiveChat((c) => ({ ...c, messages: errReply }));
      } finally {
        setLoading(false);
      }
    },
    [messages, loading, activeChatId, updateActiveChat, makeAuthenticatedRequest]
  );

  const sendFromInput = useCallback(() => {
    submitUserMessage(input);
  }, [input, submitUserMessage]);

  const submitQuickStart = useCallback(
    (action) => {
      if (!action) return;
      submitUserMessage(action.prompt, { displayText: action.displayText });
    },
    [submitUserMessage]
  );

  const handleNavigateAction = useCallback(
    (to) => {
      if (!to) return;
      setOpen(false);
      navigate(to);
    },
    [navigate]
  );

  if (!ENABLE_UI || !isAuthenticated || !isSupervisorRole(customer)) {
    return null;
  }

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-28 right-6 z-[100] inline-flex items-center gap-2 rounded-full bg-violet-600 px-4 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
          aria-label="Open assistant"
        >
          <SparklesIcon className="h-5 w-5 shrink-0" />
          <span className="hidden sm:inline">Assistant</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-[101] flex items-end justify-end pointer-events-none">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/20 pointer-events-auto backdrop-blur-[2px]"
            aria-label="Close assistant overlay"
            onClick={() => setOpen(false)}
          />

          <div
            className="relative pointer-events-auto mb-44 mr-3 sm:mr-5 flex h-[min(62vh,34rem)] max-h-[min(62vh,34rem)] w-[min(100vw-1rem,26rem)] sm:w-[26rem] flex-col overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] ring-1 ring-black/5"
            role="dialog"
            aria-labelledby="customer-assistant-title"
            aria-modal="true"
          >
            <div className="flex shrink-0 items-start justify-between gap-2 border-b border-gray-100 bg-gradient-to-b from-gray-50 to-white px-3 py-2.5 sm:px-4">
              <div className="min-w-0 flex items-start gap-2">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700 sm:h-9 sm:w-9">
                  <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
                </span>
                <div>
                  <h2 id="customer-assistant-title" className="text-[14px] font-semibold leading-tight text-gray-900 sm:text-[15px]">
                    Assistant
                  </h2>
                  <p className="mt-0.5 text-[10px] leading-snug text-gray-500 sm:text-[11px]">
                    Book appointments, manage claims & claimants — all in chat.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="shrink-0 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-row">
              <aside
                className="flex w-[6.75rem] shrink-0 flex-col border-r border-gray-100 bg-gray-50/95 py-2 pl-1.5 pr-1 sm:w-[7.75rem]"
                aria-label="Conversations"
              >
                <button
                  type="button"
                  onClick={handleNewChat}
                  className="flex w-full items-center justify-center gap-0.5 rounded-lg border border-dashed border-violet-300 bg-white py-1.5 text-[10px] font-medium text-violet-700 hover:bg-violet-50 sm:text-[11px]"
                >
                  <PlusIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
                  New
                </button>
                <div className="mt-2 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pr-0.5">
                  {sortedChats.map((chat) => {
                    const active = chat.id === activeChatId;
                    return (
                      <div
                        key={chat.id}
                        className={`group relative rounded-lg border ${
                          active
                            ? 'border-violet-200 bg-violet-50'
                            : 'border-transparent bg-white/80 hover:border-gray-200'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setActiveChatId(chat.id);
                            setInput('');
                          }}
                          className="w-full px-1.5 py-1.5 text-left text-[10px] leading-tight text-gray-800 sm:text-[11px]"
                          title={chat.title}
                        >
                          <span className="line-clamp-3 break-words">{chat.title || 'New chat'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleDeleteChat(e, chat.id)}
                          className="absolute right-0.5 top-0.5 rounded p-0.5 text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                          aria-label={`Delete ${chat.title}`}
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </aside>

              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50/50 px-2.5 py-2.5 sm:px-3">
                  {messages.length === 0 && (
                    <div className="rounded-xl border border-dashed border-gray-200 bg-white/80 px-2 py-3">
                      <p className="text-[11px] leading-relaxed text-gray-600 text-center mb-3">
                        Choose a topic to start or type your own question below. I can book
                        new appointments and create claimants/claims for you right here.
                      </p>
                      <div className="flex flex-col gap-2" role="group" aria-label="Quick start topics">
                        {QUICK_START_ACTIONS.map((action) => {
                          const { id, label, Icon } = action;
                          const highlighted = id === suggestedStarterId;
                          return (
                            <div key={id}>
                              <button
                                type="button"
                                disabled={loading}
                                onClick={() => submitQuickStart(action)}
                                className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-[12px] font-medium text-gray-800 shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                                  highlighted
                                    ? 'border-violet-400 bg-violet-50 ring-2 ring-violet-300 hover:bg-violet-100'
                                    : 'border-violet-200 bg-white hover:border-violet-300 hover:bg-violet-50'
                                }`}
                                aria-label={highlighted ? `${label} (suggested for this page)` : label}
                              >
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
                                  <Icon className="h-4 w-4" aria-hidden />
                                </span>
                                <span className="min-w-0 leading-snug">
                                  {label}
                                  {highlighted && (
                                    <span className="ml-1.5 align-middle rounded-full bg-violet-600 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                                      Suggested
                                    </span>
                                  )}
                                </span>
                              </button>
                              {id === 'status' && (
                                <button
                                  type="button"
                                  className="mt-1 w-full text-center text-[10px] font-medium text-violet-700 underline-offset-2 hover:text-violet-900 hover:underline"
                                  onClick={() => handleNavigateAction('/appointments')}
                                >
                                  Open Appointments list →
                                </button>
                              )}
                              {id === 'new_claim' && (
                                <button
                                  type="button"
                                  className="mt-1 w-full text-center text-[10px] font-medium text-violet-700 underline-offset-2 hover:text-violet-900 hover:underline"
                                  onClick={() => handleNavigateAction('/claims')}
                                >
                                  Open Claims list →
                                </button>
                              )}
                              {id === 'edit' && (
                                <button
                                  type="button"
                                  className="mt-1 w-full text-center text-[10px] font-medium text-violet-700 underline-offset-2 hover:text-violet-900 hover:underline"
                                  onClick={() => handleNavigateAction('/appointments')}
                                >
                                  Open Appointments list →
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {messages.map((m, i) => {
                    const isUser = m.role === 'user';
                    const isLast = i === messages.length - 1;
                    const parsed = !isUser ? parseOptionsBlocks(m.content) : null;
                    const visibleText = isUser
                      ? (m.displayContent || m.content)
                      : (parsed?.stripped ?? m.content);
                    const chips = !isUser ? extractActionChips(visibleText) : [];
                    const optionBlocks = !isUser && isLast ? parsed?.blocks || [] : [];
                    return (
                      <div
                        key={`${activeChatId}-${i}`}
                        className={`mb-2 flex ${isUser ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[92%] rounded-2xl px-3 py-2 shadow-sm ${
                            isUser
                              ? 'rounded-br-md bg-violet-600 text-white'
                              : 'rounded-bl-md border border-gray-200/90 bg-white text-gray-900'
                          }`}
                        >
                          {isUser ? (
                            <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{visibleText}</p>
                          ) : (
                            <>
                              <FormattedAssistantText text={visibleText} />
                              <AssistantOptionButtons
                                blocks={optionBlocks}
                                disabled={loading}
                                onPick={(opt) => submitUserMessage(opt.send, { displayText: opt.label })}
                              />
                              <AssistantActionChips chips={chips} onNavigate={handleNavigateAction} />
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {loading && (
                    <div className="flex items-center gap-2 pl-1 text-xs text-gray-500">
                      <span
                        className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600"
                        aria-hidden
                      />
                      Thinking…
                    </div>
                  )}
                  <div ref={listEndRef} />
                </div>

                <div className="shrink-0 border-t border-gray-100 bg-white p-2 sm:p-3">
                  {messages.length > 0 && (
                    <div
                      className="mb-2 -mx-0.5 flex gap-1.5 overflow-x-auto pb-1"
                      role="group"
                      aria-label="Quick actions"
                    >
                      {QUICK_START_ACTIONS.map((action) => {
                        const { id, shortLabel, label, Icon } = action;
                        const highlighted = id === suggestedStarterId;
                        return (
                          <button
                            key={id}
                            type="button"
                            disabled={loading}
                            onClick={() => submitQuickStart(action)}
                            title={label}
                            aria-label={label}
                            className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                              highlighted
                                ? 'border-violet-400 bg-violet-100 text-violet-800 ring-1 ring-violet-300'
                                : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-violet-300 hover:bg-violet-50 hover:text-violet-800'
                            }`}
                          >
                            <Icon className="h-3.5 w-3.5" aria-hidden />
                            <span>{shortLabel || label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <textarea
                      rows={2}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendFromInput();
                        }
                      }}
                      placeholder="Ask about claims or appointments…"
                      className="min-h-[44px] max-h-28 flex-1 resize-none rounded-xl border border-gray-200 bg-gray-50/80 px-2.5 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-violet-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-violet-500/20 sm:px-3"
                    />
                    <button
                      type="button"
                      onClick={sendFromInput}
                      disabled={loading || !input.trim()}
                      className="shrink-0 rounded-xl bg-violet-600 p-2.5 text-white shadow-sm transition-colors hover:bg-violet-700 disabled:opacity-40 disabled:hover:bg-violet-600"
                      aria-label="Send"
                    >
                      <PaperAirplaneIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerAssistantChat;
